/**
 * TRAINING REQUISITION SYSTEM - COMPREHENSIVE WORKFLOW DOCUMENTATION
 * 
 * OVERVIEW:
 * The Training Requisition system manages the complete lifecycle of training requests
 * from initiation through multi-level approval to final training delivery. It supports
 * both Company Employees and Contract Supervisors with different approval workflows.
 * 
 * ==================== WORKFLOW ARCHITECTURE ====================
 * 
 * APPROVAL HIERARCHY:
 * Initiator → Approver 1 (L+1) → Approver 2 (Dept. Head) → Final Approver (Training Head)
 * 
 * WORKFLOW STAGES:
 * 1. INITIATION: Employee/Supervisor creates training request
 * 2. DEPARTMENTAL APPROVAL: Department head reviews approved requests

 * 
 * ==================== STATUS MANAGEMENT SYSTEM ====================
 * 
 * DUAL STATUS ARCHITECTURE:
 * The system uses two status fields for comprehensive workflow tracking:
 * - wld_status: Overall request status
 * - wld_pendingStatus: Current approval stage
 * 
 * STATUS DEFINITIONS:
 * 
 * 1. INITIATED STATUS:
 *    - wld_status: "Initiated"
 *    - wld_pendingStatus: "ApproverFinal" (Direct to final approver)
 *    - Description: Request created and submitted for approval
 *    - Actions Available: Edit (Initiator only), Delete (Initiator only)
 *    - Next Stage: Final Approval
 * 
 * 2. IN PROGRESS STATUS:
 *    - wld_status: "In progress"
 *    - wld_pendingStatus:  "ApproverFinal"
 *    - Description: Request moving through approval workflow
 *    - Actions Available: Approve/Reject (Current approver only)
 *    - Next Stage: Depends on current approval level
 * 
 * 3. APPROVED STATUS:
 *    - wld_status: "Approved"
 *    - wld_pendingStatus: "Approved"
 *    - Description: Request fully approved and ready for training
 *    - Actions Available: View only, Training scheduling
 *    - Next Stage: Training execution
 * 
 * 4. REJECTED STATUS:
 *    - wld_status: "Rejected"
 *    - wld_pendingStatus: "Rejected"
 *    - Description: Request rejected at any approval level
 *    - Actions Available: Edit and resubmit (Initiator only)
 *    - Next Stage: Back to "Initiated" when resubmitted
 * 
 * 5. DELETED STATUS:
 *    - wld_status: "Deleted"
 *    - wld_pendingStatus: "Deleted"
 *    - Description: Request cancelled by initiator
 *    - Actions Available: None (Soft delete)
 *    - Next Stage: None (End state)
 * 
 * ==================== APPROVER ROLE DEFINITIONS ====================
 * 
 * APPROVER 1 (L+1):
 * - Role: Direct supervisor of the trainee
 * - Authority: First-level approval for training requests
 * - Responsibilities: Validate training need, resource availability
 * - Access: Can approve/reject requests from direct reports
 * 

 * 
 * FINAL APPROVER (TRAINING HEAD):
 * - Role: Head of Training Department
 * - Authority: Final approval for all training requests
 * - Source: Retrieved from TrainingRequisitionFinalApprover table
 * - Responsibilities: Training program validation, resource allocation
 * 
 * ==================== DYNAMODB STRUCTURE (FROM ROUTER IMPLEMENTATION) ====================
 * 
 * TRAINING REQUISITION SYSTEM - 3 RECORD TYPES CREATED:
 * 
 * ==================== RECORD TYPE 1: MAIN TRAINING REQUISITION ====================
 * 
 * CREATION LOCATION: TrainingRequisitionRouter.post("/") - Step 3
 * 
 * STRUCTURE:
 * - PK: "TrainingRequisition" (Fixed partition key)
 * - SK: "{userId}#{timestamp}" (Generated in frontend: `${userId}#${Math.floor(Date.now() / 1000)}`)
 * 
 * ROUTER CODE REFERENCE:
 * ```javascript
 * // Step 3: Create main training request
 * const mainRequestResult = await admSvc.addTrainingRequest(requestData);
 * ```
 * 
 * COMPLETE DYNAMODB RECORD:
 * {
 *   "PK": "TrainingRequisition",
 *   "SK": "john.doe@company.com#1640995200",
 *   "wld_TrainingReqEmployeeType": "Company Employee",
 *   "wld_TrainingReqEmployeeName": ["John Doe", "Jane Smith"],
 *   "wld_TrainingReqSalaryEmployeeCode": ["EMP001", "EMP002"],
 *   "wld_TrainingReqEmployeeEmailID": ["john.doe@company.com", "jane.smith@company.com"],
 *   "wld_TrainingReqSubject": "Welding Safety,Arc Welding",
 *   "wld_TrainingReqSubjectCode": ["SUB001", "SUB002"],
 *   "wld_TrainingReqNumber": "Company Employee_1025",
 *   "wld_TrainingReqApproverFinal": "Training Head",
 *   "wld_TrainingReqApproverFinalEmailId": "training.head@company.com",
 *   "wld_TrainingReqPendingWith": "training.head@company.com",
 *   "wld_status": "Initiated",
 *   "wld_pendingStatus": "ApproverFinal",
 *   "RevisionNo": 0,
 *   "wld_createdby": "john.doe",
 *   "wld_createdon": "15/01/2024",
 *   "wld_TrainingReqRequesterDepartment": "PED",
 *   "wld_TrainingReqRequesterName": "John Doe",
 *   "wld_TrainingReqRequesterEmailId": "john.doe@company.com",
 *   "wld_TrainingReqDate": "15/01/2024",
 *   "wld_TrainingReqCompanyName": "Godrej & Boyce Mfg. Ltd."
 * }
 * 
 * ==================== RECORD TYPE 2: CLOSURE REMARKS (APPROVAL WORKFLOW) ====================
 * 
 * CREATION LOCATION: TrainingRequisitionRouter.post("/") - Step 5
 * RESUBMISSION: TrainingRequisitionRouter.post("/workflow") - Step 3
 * 
 * STRUCTURE:
 * - PK: "{mainRequestSK}" (Same as main request SK for linking)
 * - SK: "Rev{N}" (Rev0, Rev1, Rev2... for revision tracking)
 * 
 * ROUTER CODE REFERENCE:
 * ```javascript
 * // Step 5: Create initial revision record
 * const initialRevisionRecord = {
 *   PK: requestData.SK,
 *   SK: "Rev0",
 *   wld_type: "Closure Remarks",
 *   RevisionNo: 0
 * };
 * await admSvc.addTrainingRequest(initialRevisionRecord);
 * ```
 * 
 * COMPLETE DYNAMODB RECORD:
 * {
 *   "PK": "john.doe@company.com#1640995200",
 *   "SK": "Rev0",
 *   "wld_type": "Closure Remarks",
 *   "RevisionNo": 0,
 *   "wld_status": "Approved",
 *   "wld_pendingStatus": "Approved",
 *   "wld_TrainingReqApproverFinal": "Training Head",
 *   "wld_TrainingReqApproverFinalEmailId": "training.head@company.com",
 *   "wld_TrainingReqPendingWith": "training.head@company.com",
 *   "wld_TrainingReqApproverFinalCreatedOn": "16/01/2024",
 *   "wld_TrainingReqApproverFinalComment": "Approved for Q2 training",
 *   "wld_TrainingReqApproverFinalStatus": "Approved"
 * }
 * 
 * ==================== RECORD TYPE 3: SUBJECT-EMPLOYEE TRAINING RECORDS ====================
 * 
 * CREATION LOCATION: TrainingRequisitionRouter.post("/") - Step 4
 * 
 * STRUCTURE:
 * - PK: "{subjectCode}" (Training subject code for partitioning)
 * - SK: "{mainRequestSK}#{employeeEmail}" (Composite key for unique identification)
 * 
 * ROUTER CODE REFERENCE:
 * ```javascript
 * // Step 4: Create subject-employee records
 * for (const subjectCode of subjectCodes) {
 *   const subjectEmployeeRecord = {
 *     PK: subjectCode,
 *     SK: `${requestData.SK}#${employeeEmail}`,
 *     wld_type: "Subject"
 *   };
 *   await admSvc.addTrainingRequest(subjectEmployeeRecord);
 * }
 * ```
 * 
 * COMPLETE DYNAMODB RECORD:
 * {
 *   "PK": "SUB001",
 *   "SK": "john.doe@company.com#1640995200#john.doe@company.com",
 *   "wld_type": "Subject",
 *   "wld_status": "Subject",
 *   "wld_TrainingReqNumber": "Company Employee_1025",
 *   "wld_TrainingReqEmployeeName": "John Doe",
 *   "wld_TrainingReqEmployeeEmailID": "john.doe@company.com",
 *   "wld_TrainingReqSalaryEmployeeCode": "EMP001",
 *   "wld_TrainingReqRequesterDepartment": "PED",
 *   "wld_trainingSub": "Welding Safety,Arc Welding",
 *   "wld_createdby": "john.doe",
 *   "wld_createdon": "15/01/2024",
 *   "wld_attendance": "",
 *   "wld_score": 0,
 *   "wld_marks": 0,
 *   "wld_percentage": 0,
 *   "wld_ScoreUpdate": 0
 * }
 * 
 * ==================== ADDITIONAL SYSTEM RECORDS ====================
 * 
 * TRAINING REQUEST COUNTER:
 * - PK: "TrainingRequestCount"
 * - SK: "1"
 * - ReqCount: {number} (Auto-incremented)
 * 
 * FINAL APPROVER CONFIGURATION:
 * - PK: "TrainingRequisitionFinalApprover"
 * - SK: {varies}
 * - FinalApproverName: "Training Head"
 * - FinalApproverFromEmail: "training.head@company.com"
 * 
 * ==================== REVISION NUMBER LOGIC ====================
 * 
 * REVISION CONTROL SYSTEM:
 * 
 * ==================== ROUTER-BASED WORKFLOW OPERATIONS ====================
 * 
 * CREATION WORKFLOW (POST /):
 * 
 * STEP 1: Validate request data using Training_RequisitionSchema
 * STEP 2: Get final approver and increment counter
 * STEP 3: Create main training request (PK="TrainingRequisition")
 * STEP 4: Create subject-employee records (PK=subjectCode, multiple records)
 * STEP 5: Create initial revision record (PK=mainRequestSK, SK="Rev0")
 * STEP 6: Send email notification
 * 
 * APPROVAL WORKFLOW (POST /workflow):
 * 
 * STEP 1: Update current revision with approver decision
 * STEP 2: Update main request with new status and revision number
 * STEP 3: Create new revision if resubmission (rejected → initiated)
 * STEP 4: Update all subject records if final status (approved/deleted)
 * STEP 5: Send email notification
 * 
 * QUERY PATTERNS (POST /getTrainingReqData):
 * 
 * FLAG="initiated": 
 * - Query: PK="TrainingRequisition" AND begins_with(SK, userId)
 * - Returns: User's initiated requests
 * 
 * FLAG="pending":
 * - Query: PK="TrainingRequisition" AND wld_TrainingReqPendingWith=userId (GSI)
 * - Returns: Requests pending user's approval
 * 
 * FLAG="trainreq":
 * - Query: PK="TrainingRequisition" AND SK=specificSK
 * - Returns: Specific training request
 * 
 * FLAG="reqNumber":
 * - Query: PK=requestNumber
 * - Returns: Closure remarks for specific request
 * 
 * REVISION TRACKING IMPLEMENTATION:
 * 
 * INITIAL CREATION (Router POST /):
 * ```javascript
 * // Main record
 * { PK: "TrainingRequisition", SK: "user#timestamp", RevisionNo: 0 }
 * 
 * // Initial revision
 * { PK: "user#timestamp", SK: "Rev0", RevisionNo: 0 }
 * 
 * // Subject records (one per employee per subject)
 * { PK: "subjectCode", SK: "user#timestamp#employeeEmail" }
 * ```
 * 
 * RESUBMISSION (Router POST /workflow):
 * ```javascript
 * // Update main record
 * { RevisionNo: newRevisionNumber }
 * 
 * // Create new revision
 * { PK: "user#timestamp", SK: "Rev{N+1}", RevisionNo: newRevisionNumber }
 * ```
 * 
 * RECORD RELATIONSHIPS:
 * - Main request ↔ Closure remarks: Linked via PK=mainRequestSK
 * - Main request ↔ Subject records: Linked via SK containing mainRequestSK
 * - All records share wld_TrainingReqNumber for reporting queries
 * ==================== API INTEGRATION DETAILS ====================
 * 
 * SERVICE ENDPOINTS:
 * 
 * 1. CREATE REQUEST:
 *    - Endpoint: POST /trainingrequisition/
 *    - Function: AddTrainingRequest()
 *    - Purpose: Create new training requisition
 *    - Returns: Success/Duplicate/Error status
 * 
 * 2. FETCH REQUESTS:
 *    - Endpoint: POST /trainingrequisition/getTrainingReqData
 *    - Function: getReqByType()
 *    - Filters: "trainreq", "pending", "initiated"
 *    - Purpose: Retrieve requests by type and user
 * 
 * 3. UPDATE REQUEST:
 *    - Endpoint: POST /trainingrequisition/updater
 *    - Function: updateTraining()
 *    - Purpose: Modify existing training request
 *    - Triggers: Edit operations, status changes
 * 
 * 4. WORKFLOW MANAGEMENT:
 *    - Endpoint: POST /trainingrequisition/workflow
 *    - Function: updateTrainingRequest()
 *    - Purpose: Handle approval/rejection workflow
 *    - Updates: Status, pending status, approver comments
 * 
 * 5. APPROVED TRAINING RECORDS:
 *    - Endpoint: POST /trainingrequisition/getApprovedtraining
 *    - Router: TrainingRequisitionRouter.post("/getApprovedtraining")
 *    - Query: PK=subjectCode AND wld_status="Approved"
 *    - Purpose: Retrieve approved subject-employee records with scores
 *    - Returns: Training execution data from subject-employee records
 * 
 * 6. WORKFLOW UPDATES:
 *    - Endpoint: POST /trainingrequisition/workflow
 *    - Router: TrainingRequisitionRouter.post("/workflow")
 *    - Updates: Current revision, main request, subject records (if final)
 *    - Creates: New revision record for resubmissions
 * 
 * ==================== BUSINESS RULES AND VALIDATIONS ====================
 * 
 * CREATION RULES:
 * - All fields mandatory except company name for contractors
 * - At least one training subject required
 * - Employee selection based on type (Company/Contract)
 * - Automatic requester information from session
 * 
 * EDIT RESTRICTIONS:
 * - Only initiator can edit rejected requests
 * - No editing allowed for approved/in-progress requests
 * - Delete only available for initiated requests
 * 
 * APPROVAL AUTHORITY:
 * - Initiators cannot approve their own requests
 * - Approvers can only act on requests in their queue
 * - Final approver has override capabilities
 * 
 * NOTIFICATION SYSTEM:
 * - Email notifications at each approval stage
 * - Reminder notifications for pending approvals
 * - Status change notifications to initiator
 * 
 * ==================== USER INTERFACE COMPONENTS ====================
 * 
 * TAB STRUCTURE:
 * 1. "Requests pending for your Approval" - TrainingReqApproval component
 * 2. "Requests sent for Approval" - TrainingReqInitiated component
 * 
 * GRID FEATURES:
 * - Status-based color coding
 * - Action buttons based on user role
 * - Export functionality for reporting
 * - Global search across all fields
 * 
 * FORM FEATURES:
 * - Dynamic field enabling based on employee type
 * - Multi-select popups for employees and subjects
 * - Real-time validation with error messages
 * - Reset functionality for new requests
 * 
 * ==================== INTEGRATION POINTS ====================
 * 
 * USER MANAGEMENT:
 * - Session-based user identification
 * - Role-based access control
 * - Department-based filtering
 * 
 * EMPLOYEE DATABASE:
 * - PED User integration for company employees
 * - Role-based supervisor lookup
 * - Email domain extraction and validation
 * 
 * TRAINING MASTER DATA:
 * - Subject master integration
 * - Faculty assignment system
 * - Training calendar integration
 * 
 * APPROVAL WORKFLOW:
 * - Multi-level approval routing
 * - Configurable approval hierarchy
 * - Audit trail maintenance
 */

import { useState, useEffect } from "react";
import { Button, Col, Form, Modal, Row, Alert } from "react-bootstrap";
import SuccessAlertModal from "../../Modals/successAlertModal";
import ConfirmAlertModal from "../../Modals/confirmAlertModal";
import { isAlphanumericWithSpecialChars, isAlphanumericWithSpace, isAlphanumericWithSpaceDotAmpsDashBrackets } from "../../../functions/validations";
import { showSuccess, showError, showWarning } from "../../utility/ui/notifications/Toaster";
import { AddTrainingRequest, getReqByType } from "../../../Services/trainingRequisition-service";
import { getAllUsersByRole } from "../../../Services/user-service";
import { RenderIf, formatDateDDMMYYYY, getDefaultDate } from "../../../functions/welding-functions";
import MultiValuePopUp from "../../utility/ui/modals/MultiValuePopUp";
import { BiSelectMultiple } from "react-icons/bi";
import ClosureRemarks from "../../utility/common/helpers/ClosureRemarks";


const Mandtoryheading = () => {
  return (
    <span style={{ color: "red", fontSize: "12px" }}>(All input fields are mandatory)</span>
  )
}

const AddEditTrainingRequisition = (props) => {
  const todayDate = getDefaultDate();
  let action = props.action;
  let formName = "TrainingRequisition";
  const [showSuccessModel, setShowSuccessModel] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showConfirmModel, setshowConfirmModel] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [selectedGridData, setSelectedGridData] = useState([]);
  let userId = sessionStorage.getItem("User");
  let username = sessionStorage.getItem("UserName");
  let department = "PED";
  const [Flg, setFlg] = useState(false);

  const TrainingReqEmployeeType = {
    Contractor: "Contract Supervisor",
    Employee: "Company Employee",
  };

  const [subjectArr, setSubjectArr] = useState([]);
  const [contractorSupervisorL1, setcontractorSupervisor] = useState([
    { um_email: "NA", um_name: TrainingReqEmployeeType.Contractor },
  ]);

  const [popUp, setPopUp] = useState({
    name: "",
    type: "",
    key: "",
    value: "",
  });

  const getTraningReq = async (SK) => {
    try {
      let tmpObj = { fieldValue: SK, flag: "trainreq" };
      const Records = await getReqByType(tmpObj);
      if (Records && Records.length > 0) {
        setTrainReqObj(Records[0]);
        return Records[0];
      }
    } catch (error) {
      console.error('Error fetching training request:', error);
      showError('Failed to fetch training request');
    }
  };

  const [trainReqObj, setTrainReqObj] = useState([]);
  // ===============================Edit==============================
  useEffect(() => {
    if (action == "Edit") {
      Promise.resolve(getTraningReq(props.rowData.SK));
    } else {
      setTrainReqObj({
        wld_TrainingReqEmployeeType: "Company Employee",
        wld_TrainingReqSalaryEmployeeCode: [],
        wld_TrainingReqEmployeeName: [],
        wld_TrainingReqEmployeeEmailID: [],
        wld_TrainingReqCompanyName: "Godrej & Boyce Mfg. Ltd.",
        wld_TrainingReqSubject: "",
        wld_TrainingReqSubjectCode: "",
        wld_TrainingReqDate: formatDateDDMMYYYY(todayDate),
        wld_TrainingReqRequesterName: username,
        wld_TrainingReqRequesterEmailId: userId,
        wld_TrainingReqRequesterDepartment: department,
        wld_TrainingReqNumber: "",
        wld_createdon: "",
        wld_createdby: username,
        wld_TrainingReqApproverFinal: "",
        wld_TrainingReqApproverFinalEmailId: "",
        wld_TrainingReqPendingWith: "",
        wld_TrainingReqApproverFinalComment: "",
        wld_TrainingReqApproverFinalStatus: "",
      });
    }

  }, []);

  useEffect(() => {
    if (action == "Edit" && trainReqObj.length != 0) {
      setFlg(true);
    }
  }, [trainReqObj]);

  const [error, setError] = useState({
    SK: {},
    PK: {},
    wld_TrainingReqEmployeeType: {},
    wld_TrainingReqSalaryEmployeeCode: {},
    wld_TrainingReqEmployeeName: {},
    wld_TrainingReqRequesterDepartment: {},
    wld_TrainingReqSubject: {},
    wld_createdby: {},
    wld_createdon: {},
    wld_TrainingReqCompanyName: {},

    wld_TrainingReqDate: {},
    wld_TrainingReqRequesterName: {},
  });

  const callReload = () => {
    props.onHide();
    props.setMainReload(true);
  };

  const handleCloseSuccess = () => {
    setShowSuccessModel(false);
    props.onHide();
  };
  const handleCloseConf = () => {
    setshowConfirmModel(false);
  };
  const handleEmployeeTypeChange = (value) => {
    const isEmployee = value === TrainingReqEmployeeType.Employee;
    const compName = isEmployee ? "Godrej & Boyce Mfg. Ltd." : "";
    const salaryCode = isEmployee ? [] : "NA";

    setTrainReqObj((prevState) => ({
      ...prevState,
      wld_TrainingReqSalaryEmployeeCode: salaryCode,
      wld_TrainingReqEmployeeName: [],
      wld_TrainingReqEmployeeEmailID: [],
      wld_TrainingReqCompanyName: compName,
    }));

    if (!isEmployee) {
      getContractSupervisor();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // For Contract Supervisor employee name, store as single-element array and generate codes
    if (name === "wld_TrainingReqEmployeeName" && trainReqObj.wld_TrainingReqEmployeeType === "Contract Supervisor") {
      const timestamp = Math.floor(Date.now() / 1000);
      setTrainReqObj((prevState) => ({
        ...prevState,
        wld_TrainingReqEmployeeName: [value],
        wld_TrainingReqSalaryEmployeeCode: [timestamp.toString()],
        wld_TrainingReqEmployeeEmailID: [timestamp.toString()]
      }));
    } else {
      setTrainReqObj((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    }

    if (name === "wld_TrainingReqEmployeeType") {
      handleEmployeeTypeChange(value);
    }
  };


  const showPopUp = (name, type, key, value) => {
    setPopUp({ name, type, key, value });
    setShowForm(true);
  };

  const setPopUpData = (data) => {
    if (!data || !data.result) return;
    if (data.name === "Training Subject and Date") {
      setSubjectArr(data.result);
      const vs1 = data.result
        .map(item => item?.wld_trainingSub || '')
        .filter(Boolean)
        .join(',');

      const SubjectPKArr = data.result
        .map(item => item?.SK)
        .filter(Boolean);

      setTrainReqObj((prevState) => ({
        ...prevState,
        wld_TrainingReqSubject: vs1,
        wld_TrainingReqSubjectCode: SubjectPKArr,
      }));
      setSelectedGridData(data.result);
    }

    if (data.name === "PED User") {
      const empCodesArr = data.result.map(item => item?.empSalCode || '');
      const empNamesArr = data.result.map(item => item?.empshortName || '');
      const empEmailsArr = data.result.map(item =>
        item?.empEmailId ? item.empEmailId.split("@")[0] : ''
      );

      setTrainReqObj((prevState) => ({
        ...prevState,
        wld_TrainingReqSalaryEmployeeCode: empCodesArr,
        wld_TrainingReqEmployeeName: empNamesArr,
        wld_TrainingReqEmployeeEmailID: empEmailsArr,
      }));
      setSelectedGridData(data.result);
    }
    setPopUp({ name: "", type: "", key: "", value: "" });
    setShowForm(false);
  };

  // =====================Reset form=====================================================
  const resetForm = () => {
    if (action === "Add") {
      setTrainReqObj((prevState) => ({
        ...prevState,
        wld_TrainingReqEmployeeType: "Company Employee",
        wld_TrainingReqSalaryEmployeeCode: [],
        wld_TrainingReqEmployeeName: [],
        wld_TrainingReqEmployeeEmailID: [],
        SK: "",
        PK: "",
        wld_TrainingReqCompanyName: "Godrej & Boyce Mfg. Ltd.",
        wld_TrainingReqSubject: "",
        wld_TrainingReqSubjectCode: "",
        wld_TrainingReqDate: formatDateDDMMYYYY(todayDate),
        wld_TrainingReqRequesterName: username,
        wld_TrainingReqRequesterEmailId: userId,
        wld_TrainingReqRequesterDepartment: department,
        wld_TrainingReqNumber: "",
        wld_createdon: "",
        wld_createdby: username,
      }));
      setSubjectArr([]);
    }

    setError((prevState) => ({
      ...prevState,
      SK: {},
      PK: {},
      wld_TrainingReqEmployeeType: {},
      wld_TrainingReqSalaryEmployeeCode: {},
      wld_TrainingReqEmployeeName: {},
      wld_TrainingReqRequesterDepartment: {},
      wld_TrainingReqSubject: {},
      wld_createdby: {},
      wld_createdon: {},
      wld_TrainingReqCompanyName: {},
      wld_TrainingReqDate: {},
      wld_TrainingReqRequesterName: {},
    }));
  };

  const validateTrainingRequest = () => {
    let isError = false;
    const error = {};

    if (trainReqObj.wld_TrainingReqEmployeeType === "") {
      error.wld_TrainingReqEmployeeType = {
        status: true,
        message: "Employee Type is required.",
      };
      isError = true;
    }
    if (
      (!trainReqObj.wld_TrainingReqSalaryEmployeeCode || trainReqObj.wld_TrainingReqSalaryEmployeeCode.length === 0) &&
      trainReqObj.wld_TrainingReqEmployeeType == "Company Employee"
    ) {
      error.wld_TrainingReqSalaryEmployeeCode = {
        status: true,
        message: "Please select at least one Employee Code.",
      };
      isError = true;
    }
    if (!trainReqObj.wld_TrainingReqEmployeeName ||
      (Array.isArray(trainReqObj.wld_TrainingReqEmployeeName) && trainReqObj.wld_TrainingReqEmployeeName.length === 0)) {
      error.wld_TrainingReqEmployeeName = {
        status: true,
        message: "Employee Name is required.",
      };
      isError = true;
    }
    if (trainReqObj.wld_TrainingReqCompanyName.trim() === "") {
      error.wld_TrainingReqCompanyName = {
        status: true,
        message: "Company Name is required.",
      };
      isError = true;
    }

    if (subjectArr.length === 0) {
      error.wld_TrainingReqSubject = {
        status: true,
        message: "Atleast one Subject is required.",
      };
      isError = true;
    }
    if (trainReqObj.wld_TrainingReqRequesterDepartment.trim() === "") {
      error.wld_TrainingReqRequesterDepartment = {
        status: true,
        message: "Requester Department is required.",
      };
      isError = true;
    }



    setError((prevState) => ({
      ...prevState,
      ...error,
    }));

    return isError;
  };

  // ============================ ADD =====================================
  const handleAdd = async () => {
    try {
      if (action === "Add") {
        const requestData = {
          ...trainReqObj,
          wld_status: "Initiated",
          wld_pendingStatus: "ApproverFinal",
          wld_TrainingReqNumber: trainReqObj.wld_TrainingReqEmployeeType,
          PK: "TrainingRequisition",
          SK: `${userId}#${Math.floor(Date.now() / 1000)}`,
          wld_createdby: username,
          wld_createdon: new Date().toLocaleDateString("en-GB"),
          wld_TrainingReqPendingWith: trainReqObj.wld_TrainingReqApproverFinalEmailId
        };

        const result = await AddTrainingRequest(requestData);

        if (result.message === "Duplicate") {
          showWarning(result.message);
        } else if (result.message === "Success") {
          showSuccess('Training Request has been initiated successfully');
        }

        props.setMainReload(!props.mainReload);
        setShowForm(false);
        props.onHide();
      }
    } catch (error) {
      console.error("Error adding training request:", error);
      showError("Failed to add training request");
    }
  };

  function funcclose(closebool, param2) {
    if (closebool && param2 === "Success") {
      setShowSuccessModel(true);
      props.close(true);
    }
    if (closebool && param2 === "Cancel") {
      props.onHide();
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    let result = validateTrainingRequest();
    if (!result) {
      setshowConfirmModel(true);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setAlertMessage("");
    }, 10000);
    return () => clearTimeout(timer);
  }, [alertMessage]);

  const getContractSupervisor = async () => {
    try {
      const roles = [
        { role: process.env.REACT_APP_ROLE_Manufacturingsupervisor },
        { role: process.env.REACT_APP_ROLE_WEDLINGENGINEER },
      ];
      const result = await getAllUsersByRole(roles);
      const initialvalues = [
        { um_email: "NA", um_name: TrainingReqEmployeeType.Contractor },
        ...(result || [])
      ];
      setcontractorSupervisor(initialvalues);
    } catch (error) {
      console.error('Error fetching contract supervisors:', error);
    }
  };

  useEffect(() => {
    if (trainReqObj.wld_TrainingReqSubject !== "") {
      setError((prevError) => ({
        ...prevError,
        wld_TrainingReqSubject: {
          status: false,
          message: "",
        },
      }));
    }
  }, [trainReqObj.wld_TrainingReqSubject]);

  useEffect(() => {
    if (trainReqObj.wld_TrainingReqSalaryEmployeeCode && trainReqObj.wld_TrainingReqSalaryEmployeeCode.length > 0) {
      setError((prevError) => ({
        ...prevError,
        wld_TrainingReqSalaryEmployeeCode: {
          status: false,
          message: "",
        },
        wld_TrainingReqEmployeeName: {
          status: false,
          message: "",
        },
      }));
    }
  }, [trainReqObj.wld_TrainingReqSalaryEmployeeCode]);

  return (
    <>
      <SuccessAlertModal
        msg={`Closure Remarks Updated successfully.`}
        show={showSuccessModel}
        handleClose={handleCloseSuccess}
      />
      <ConfirmAlertModal
        action={action}
        handleYes={handleAdd}
        formName={formName}
        show={showConfirmModel}
        handleClose={handleCloseConf}
      />
      <Modal
        {...props}
        size="lg"
        centered
        className={
          showForm || showSuccessModel || showConfirmModel
            ? "opacity-0 userMasterModal"
            : "opacity-100 userMasterModal"
        }
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {action === "Add"
              ? "Add Training Request"
              : "Training Request Number"}{" "}
            {action === "Add" && <Mandtoryheading />}
          </Modal.Title>
        </Modal.Header>

        <Form onSubmit={handleSubmit} autoComplete="off">
          <Modal.Body>
            <div className="px-2 pb-2">
              <Row>
                <Col md={4}>
                  <div className="m-input" style={{ width: 300 }}>
                    <span>Employee Type</span>
                    <div className="d-flex mb-2">
                      <div>
                        <input
                          type="radio"
                          name="wld_TrainingReqEmployeeType"
                          value="Company Employee"
                          disabled={action !== "Add"}
                          checked={
                            trainReqObj.wld_TrainingReqEmployeeType ===
                            TrainingReqEmployeeType.Employee
                          }
                          onChange={(e) => handleChange(e)}
                          style={{
                            fontSize: 10,
                            border: 20,
                            height: 10,
                          }}
                        />
                        <span
                          className={
                            trainReqObj.wld_TrainingReqEmployeeType ===
                              TrainingReqEmployeeType.Employee
                              ? "fontWeight "
                              : ""
                          }
                          style={{ fontSize: 12 }}
                        >
                          Company Employee
                        </span>
                      </div>
                      <div className="ms-3">
                        <input
                          type="radio"
                          name="wld_TrainingReqEmployeeType"
                          value="Contract Supervisor"
                          disabled={action !== "Add"}
                          checked={
                            trainReqObj.wld_TrainingReqEmployeeType ===
                            TrainingReqEmployeeType.Contractor
                          }
                          onChange={(e) => handleChange(e)}
                          style={{
                            fontSize: 10,
                            border: 20,
                            height: 10,
                          }}
                        />
                        <span
                          className={
                            trainReqObj.wld_TrainingReqEmployeeType ===
                              TrainingReqEmployeeType.Contractor
                              ? "fontWeight"
                              : ""
                          }
                          style={{ fontSize: 12 }}
                        >
                          Contract Supervisor
                        </span>
                      </div>
                    </div>
                  </div>
                </Col>
                {action === "Add" &&
                  trainReqObj.wld_TrainingReqEmployeeType ===
                  TrainingReqEmployeeType.Employee && (
                    <Col md={4} style={{ height: "10px" }}>
                      <Form.Group>
                        <div className="g-input" style={{ width: "100%" }}>
                          <input
                            required
                            style={{ height: "45px", width: "100%" }}
                            id="wld_TrainingReqSalaryEmployeeCode"
                            name="wld_TrainingReqSalaryEmployeeCode"
                            type="text"
                            maxLength="100"
                            readOnly
                            className={
                              error.wld_TrainingReqSalaryEmployeeCode.status
                                ? "placeHolderInput inputText text-field-with-error"
                                : "placeHolderInput inputText"
                            }
                            placeholder=" "
                            color="var(--col-707070)"
                            value={
                              Array.isArray(trainReqObj.wld_TrainingReqSalaryEmployeeCode)
                                ? trainReqObj.wld_TrainingReqSalaryEmployeeCode.join(", ")
                                : trainReqObj.wld_TrainingReqSalaryEmployeeCode
                            }
                            onChange={handleChange}
                          />
                          <Form.Label htmlFor="wld_TrainingReqSalaryEmployeeCode">
                            Salary / Employee Code
                          </Form.Label>
                          <BiSelectMultiple
                            size={20}
                            onClick={() => {
                              showPopUp(
                                "PED User",
                                "Multiple",
                                "emailid",
                                trainReqObj.wld_TrainingReqSalaryEmployeeCode
                              );
                            }}
                            color={"gray"}
                            style={{
                              marginLeft: "15.5rem",
                              marginTop: "-4.2rem",
                            }}
                          />
                        </div>
                        {error.wld_TrainingReqSalaryEmployeeCode.status && (
                          <Form.Text className="text-danger">
                            <span
                              style={{
                                position: "relative",
                                bottom: "35px",
                                fontSize: "12px",
                              }}
                            >
                              {error.wld_TrainingReqSalaryEmployeeCode.message}
                            </span>
                          </Form.Text>
                        )}
                      </Form.Group>
                    </Col>
                  )}
                <Col md={4}>
                  <Form.Group>
                    <div className="g-input" style={{ width: "100%" }}>
                      <input
                        type="text"
                        required
                        id="wld_TrainingReqEmployeeName"
                        name="wld_TrainingReqEmployeeName"
                        placeholder=" "
                        readOnly={
                          action === "Edit" ||
                          trainReqObj.wld_TrainingReqEmployeeType ===
                          TrainingReqEmployeeType.Employee
                        }
                        maxLength="50"
                        className={
                          error.wld_TrainingReqEmployeeName.status
                            ? " g-input placeHolderInput inputText text-field-with-error"
                            : "g-input placeHolderInput inputText"
                        }
                        value={
                          Array.isArray(trainReqObj.wld_TrainingReqEmployeeName)
                            ? (trainReqObj.wld_TrainingReqEmployeeType === "Contract Supervisor"
                              ? trainReqObj.wld_TrainingReqEmployeeName[0] || ""
                              : trainReqObj.wld_TrainingReqEmployeeName.join(", "))
                            : trainReqObj.wld_TrainingReqEmployeeName
                        }
                        onChange={handleChange}
                        onInput={(e) => {
                          let error = isAlphanumericWithSpace(e.target.value);
                          setError((prevState) => ({
                            ...prevState,
                            wld_TrainingReqEmployeeName: error,
                          }));
                        }}
                      />
                      <Form.Label htmlFor="wld_TrainingReqEmployeeName">
                        Employee Name
                      </Form.Label>
                    </div>
                    {error.wld_TrainingReqEmployeeName.status && (
                      <Form.Text className="text-danger">
                        <span
                          style={{
                            position: "relative",
                            bottom: "20px",
                            fontSize: "12px",
                          }}
                        >
                          {error.wld_TrainingReqEmployeeName.message}
                        </span>
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group>
                    <div className="g-input" style={{ width: "100%" }}>
                      <input
                        style={{ height: "45px", width: "100%" }}

                        id="wld_TrainingReqCompanyName"
                        required
                        name="wld_TrainingReqCompanyName"
                        type="text"
                        maxLength="100"
                        className={
                          error.wld_TrainingReqCompanyName.status
                            ? "placeHolderInput inputText text-field-with-error"
                            : "placeHolderInput inputText"
                        }
                        placeholder=" "
                        value={trainReqObj.wld_TrainingReqCompanyName}
                        readOnly={
                          action !== "Add" ||
                          trainReqObj.wld_TrainingReqEmployeeType ===
                          "Company Employee"
                        }
                        onChange={handleChange}
                        onInput={(e) => {
                          let error = isAlphanumericWithSpaceDotAmpsDashBrackets(
                            e.target.value
                          );
                          setError((prevState) => ({
                            ...prevState,
                            wld_TrainingReqCompanyName: error,
                          }));
                        }}
                      />
                      <Form.Label
                        htmlFor="wld_TrainingReqCompanyName"
                      >
                        Company Name
                      </Form.Label>
                    </div>
                    {error.wld_TrainingReqCompanyName.status && (
                      <Form.Text className="text-danger">
                        <span
                          style={{
                            position: "relative",
                            bottom: "10px",
                            fontSize: "12px",
                          }}
                        >
                          {error.wld_TrainingReqCompanyName.message}
                        </span>
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>

                <Col md={4} style={{ height: "60px" }}>
                  <Form.Group>
                    <div className="g-input" style={{ width: "100%" }}>
                      <input
                        readOnly
                        id="wld_TrainingReqSubject"
                        name="wld_TrainingReqSubject"
                        type="text"
                        placeholder=" "
                        color="var(--col-707070)"
                        style={{ width: "100%" }}
                        value={trainReqObj.wld_TrainingReqSubject}
                      />
                      <Form.Label htmlFor="wld_TrainingReqSubject">
                        Training Subject & Date
                      </Form.Label>
                      {action === "Add" && (
                        <BiSelectMultiple
                          size={20}
                          onClick={() => {
                            showPopUp(
                              "Training Subject and Date",
                              "Multiple",
                              "Training Subject",
                              popUp.value
                            );
                          }}
                          color={"gray"}
                          style={{
                            marginLeft: "15.5rem",
                            marginTop: "-4.1rem",
                          }}
                        />
                      )}
                    </div>
                    {error.wld_TrainingReqSubject?.status && (
                      <Form.Text className="text-danger">
                        <span style={{ fontSize: "12px" }}>
                          {error.wld_TrainingReqSubject.message}
                        </span>
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>


                <Col md={4} >
                  <Form.Group>
                    <div className="g-input" style={{ width: "100%" }}>
                      <input
                        style={{ width: "100%" }}
                        id="wld_TrainingReqDate"
                        readOnly
                        name="wld_TrainingReqDate"
                        type="text"
                        maxLength="20"
                        className={
                          error.wld_TrainingReqDate.status
                            ? "placeHolderInput inputText text-field-with-error"
                            : "placeHolderInput inputText"
                        }
                        placeholder=" "
                        value={trainReqObj.wld_TrainingReqDate}
                      />
                      <Form.Label
                        htmlFor="wld_TrainingReqDate"
                      >
                        Training Requisition Date
                      </Form.Label>
                    </div>
                    {error.wld_TrainingReqDate.status && (
                      <Form.Text className="text-danger">
                        {error.wld_TrainingReqDate.message}
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
                <Col md={4} >
                  <Form.Group>
                    <div className="g-input" style={{ width: "100%", position: "relative" }}>
                      <input
                        style={{ height: "45px", width: "100%" }}

                        readOnly
                        id="wld_TrainingReqRequesterName"
                        name="wld_TrainingReqRequesterName"
                        type="text"
                        maxLength="20"
                        className={
                          error.wld_TrainingReqRequesterName.status
                            ? "placeHolderInput inputText text-field-with-error"
                            : "placeHolderInput inputText"
                        }
                        placeholder=" "
                        value={trainReqObj.wld_TrainingReqRequesterName}
                      />
                      <Form.Label
                        htmlFor="wld_TrainingReqRequesterName"
                      >
                        Requester Name
                      </Form.Label>
                    </div>
                    {error.wld_TrainingReqRequesterName.status && (
                      <Form.Text className="text-danger">
                        {error.wld_TrainingReqRequesterName.message}
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group>
                    <div className="g-input" style={{ width: "100%" }}>
                      <input
                        style={{ height: "45px", width: "100%" }}

                        id="wld_TrainingReqRequesterDepartment"
                        name="wld_TrainingReqRequesterDepartment"
                        type="text"
                        maxLength="20"
                        className={
                          error.wld_TrainingReqRequesterDepartment.status
                            ? "placeHolderInput inputText text-field-with-error"
                            : "placeHolderInput inputText"
                        }
                        placeholder=" "
                        value={trainReqObj.wld_TrainingReqRequesterDepartment}
                        readOnly={action !== "Add"}
                        onChange={handleChange}
                        onInput={(e) => {
                          let error = isAlphanumericWithSpecialChars(
                            e.target.value
                          );
                          setError((prevState) => ({
                            ...prevState,
                            wld_TrainingReqRequesterDepartment: error,
                          }));
                        }}
                      />
                      <Form.Label
                        // className="required-field"
                        htmlFor="wld_TrainingReqRequesterDepartment"
                      >
                        Requester Department
                      </Form.Label>
                    </div>
                    {error.wld_TrainingReqRequesterDepartment.status && (
                      <Form.Text className="text-danger">
                        {error.wld_TrainingReqRequesterDepartment.message}
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
              </Row>
            </div>
            {alertMessage && (
              <Alert className="alertBox" variant="warning">
                {alertMessage}
              </Alert>
            )}
            {action === "Add" && (
              <Row>
                <div>
                  <div
                    className={
                      action === "Edit"
                        ? "float-start m-1 invisible"
                        : "float-start m-1 visible"
                    }
                  >
                    <Button variant="outline-primary" onClick={resetForm} style={{ width: "100px", height: "38px", position: "relative", top: "7px" }}>
                      Reset
                    </Button>
                  </div>
                  <div className="float-end m-1">
                    <Button type="submit" className="btn-add-custom" style={{ width: "100px", height: "38px" }}>
                      {action === "Edit" ? "Update" : "Add"}
                    </Button>
                  </div>
                </div>
              </Row>
            )}
            {action === "Edit" && Flg && (
              <div>
                <Row>
                  <br />
                </Row>
                <Row>
                  <u style={{ position: "relative", left: "9px" }}>Closure Remarks </u>
                  <ClosureRemarks
                    close={funcclose}
                    rowData={trainReqObj}
                    callReload={callReload}
                    calledFrom={props.calledFrom}
                    setMainReload={props.setMainReload}
                  />
                </Row>
              </div>
            )}
          </Modal.Body>
        </Form>
      </Modal >
      <RenderIf isTrue={popUp.type === "Multiple" && showForm && action === "Add"}>
        <MultiValuePopUp
          show={popUp.type === "Multiple"}
          onHide={() => {
            setPopUp({ name: "", type: "", key: "", value: "" });
            setShowForm(false);
          }}
          setSelected={setPopUpData}
          masterName={popUp.name}
          paramkey={popUp.key}
          paramvalue={popUp.value}
          selectedData={selectedGridData}
          backdrop="static"
        />
      </RenderIf>


    </>
  );
};

export default AddEditTrainingRequisition;